import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { type EnhancedToolResult, type ToolExecutionContext } from '../types';
import { DesignTool, designTargetSchemaFields } from './designTargeting';
import { applyHtmlAndLint, checkpointDesign } from './htmlAssetToolHelpers';

import { getHtmlDesignPlan, getSceneTechniques } from 'js/src/data/html-design';

export const writeHtmlAssetSchema = z.object({
    ...designTargetSchemaFields,
    html: z
        .string()
        .describe(
            'Complete <!DOCTYPE html>...</html> document with an inline, paused ' +
                'GSAP timeline registered at window.__timelines.main.',
        ),
});
export type WriteHtmlAssetArgs = z.infer<typeof writeHtmlAssetSchema>;

export class WriteHtmlAssetTool extends DesignTool<typeof writeHtmlAssetSchema> {
    protected override readonly schema = writeHtmlAssetSchema;
    protected override readonly toolName = TOOL_NAMES.writeHtmlAsset;
    protected override readonly toolDescription =
        'Replace the entire composition with a new self-contained HTML document for ' +
        'the dynamic graphic. Use this to author the first version or when restructuring ' +
        'so much that most of the markup changes; for a smaller change to an existing ' +
        `design, prefer ${TOOL_NAMES.editHtmlAsset} (far fewer tokens, less likely to drop unrelated ` +
        'markup). The document must register a paused, seekable GSAP ' +
        'timeline at window.__timelines.main, animate only via GSAP (no CSS ' +
        'animations/transitions), be deterministic (no Date.now/Math.random), and ' +
        'reference template colors via var(--l5-color-N). Returns lint findings; fix ' +
        'any errors and call again before replying to the user.';

    public getToolDisplayName(): string {
        return 'Write design';
    }

    public get statusIcon(): string {
        return 'code';
    }

    public async execute(
        args: WriteHtmlAssetArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const resolved = this.resolveTarget(args.scene_id);
        if ('error' in resolved) {
            return resolved.error;
        }
        const { asset, scene } = resolved;
        const outcome = await applyHtmlAndLint(
            this.luminary,
            asset,
            args.html,
            scene.duration,
            context.conversationId,
            getHtmlDesignPlan(this.getState(), scene.id),
            getSceneTechniques(this.getState(), scene.id),
        );
        // Committed even when lint errors, so checkpoint either way or a graphic left mid-fix is lost.
        checkpointDesign(this.luminary, this.dispatch, context.parentActionGroup);
        return outcome;
    }
}
